import { Nullable } from '@/utils/types';
import { AvailabilityApproveErrorCodeEnum, IApproveAvailabilityError, IAvailabilityPropertyCandidate } from '@/bundles/AvailabilityIngestion/types';

/**
 * `approve` refuses with a structured body on 409 (candidate list) and 400
 * (missing address fields) — the Nest exception filter nests it at
 * `error.response.data.error.response` (`{ code, message, details }`), see
 * `src/filters/http-exception.filter.ts` in the nest worktree. Pure — no Vue,
 * no store — so it is safe to call from a service catch block.
 */
interface IApproveErrorBody {
  code?: string;
  message?: string;
  details?: {
    candidates?: IAvailabilityPropertyCandidate[];
    missing_fields?: string[];
  };
}

interface IApproveErrorResponseData {
  message?: string;
  error?: { response?: IApproveErrorBody };
}

/** The axios error shape this reads, narrowed from `unknown` rather than cast through `any`. */
interface IApproveAxiosError {
  response?: { status?: number; statusText?: string; data?: unknown };
  message?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

export const toApproveError = (error: unknown): IApproveAvailabilityError => {
  const axiosError: IApproveAxiosError = isRecord(error) ? error : {};
  const status = axiosError.response?.status ?? 0;
  const data: IApproveErrorResponseData = isRecord(axiosError.response?.data) ? axiosError.response?.data : {};
  const structured = data.error?.response;

  const rawCode: Nullable<string> = structured?.code ?? null;
  const code = rawCode && (Object.values(AvailabilityApproveErrorCodeEnum) as string[]).includes(rawCode)
    ? (rawCode as AvailabilityApproveErrorCodeEnum)
    : null;

  const candidates: IAvailabilityPropertyCandidate[] = structured?.details?.candidates ?? [];
  const missingFields: string[] = structured?.details?.missing_fields ?? [];
  const message = structured?.message || data.message || axiosError.message || '';
  // Mirrors `ErrorMapper.mapToCodeModel` — `AppNotification` renders `title`
  // unconditionally, so the non-structured failure path (`confirmApprove`
  // still calls `addNotification(error)` for it) needs a header too.
  const title = axiosError.response?.statusText || 'Error';

  return {
    status,
    message,
    title,
    code,
    candidates,
    missingFields,
  };
};
